import { createDraftSafeSelector as createSelector } from '@reduxjs/toolkit';

import { Inject } from '@vk-hr-tek/core/ioc';

import { RootState } from '@app/store';

import {
  CalendarMapper,
  DashboardMapper,
  QuickActionsMapper,
  PartitionMapper,
  PersonalLinksSettingsMapper,
  WidgetBannerMapper,
  SurveysWidgetListMapper,
  QuickActionsSettingsMapper,
  TopNewsMapper,
  PostsFeedMapper,
  AbsencesWidgetMapper,
  WidgetTaskListMapper,
  BirthdaysMapper,
} from '../mappers';

export const selectDashboardStatus = (state: RootState) =>
  state.dashboard.status;

export const selectDashboardQuickActionsStatus = (state: RootState) =>
  state.dashboard.quickActions.status;

export const selectDashboardError = (state: RootState) => state.dashboard.error;

export const selectDashboardLayout = createSelector(
  [
    (state: RootState) => state.dashboard.layout,
    (state: RootState) => !!state.user.data?.has_company_side,
    (_: RootState, inject: Inject) => inject(DashboardMapper),
  ],
  (widgets, hasCompanySide, format) =>
    format.processLayout(widgets, hasCompanySide ? [] : ['functional']),
);

export const selectDashboardCompanyQuickActions = createSelector(
  [
    (state: RootState) => state.dashboard.quickActions.companyActions,
    (state: RootState) => !!state.user.data?.has_company_side,
    (_: RootState, inject: Inject) => inject(QuickActionsMapper),
  ],
  (quickActions, hasCompanySide, format) =>
    format
      .processQuickActions(quickActions)
      .filter((task) => hasCompanySide || task.side === 'employee')
      .filter(
        (task) =>
          task.side === 'company' || !window.REACT_APP_VKHRTEK_LK_DISABLED,
      ),
);

export const selectAbsencesWidgetList = createSelector(
  [
    (state: RootState) => state.dashboard.absences.items,
    (_: RootState, inject: Inject) => inject(AbsencesWidgetMapper),
  ],
  (employees, format) =>
    employees.map((employee) => format.processAbsenceEmployeeItem(employee)),
);

export const selectAbsencesWidgetStatus = (state: RootState) =>
  state.dashboard.absences.status;

export const selectAbsencesWidgetIsManager = (state: RootState) =>
  state.dashboard.absences.isManager;

export const selectDashboardUserQuickActions = (state: RootState) =>
  state.dashboard.quickActions.userActions;

export const selectElectronicServicesStatus = (state: RootState) =>
  state.dashboard.electronicServices.status;

export const selectElectronicServices = (state: RootState) =>
  state.dashboard.electronicServices.items;

export const selectPersonalLinksStatus = (state: RootState) =>
  state.dashboard.personalLinks.status;

export const selectPersonalLinks = (state: RootState) =>
  state.dashboard.personalLinks.items;

export const selectTaskList = createSelector(
  [
    (state: RootState) => state.dashboard.taskList.items,
    (_: RootState, inject: Inject) => inject(WidgetTaskListMapper),
  ],
  (partitions, format) => format.processTaskListItems(partitions),
);

export const selectTaskListStatus = (state: RootState) =>
  state.dashboard.taskList.status;

export const selectNewsListStatus = (state: RootState) =>
  state.dashboard.newsList.status;

export const selectNewsList = (state: RootState) =>
  state.dashboard.newsList.items;

export const selectEmployeePartitionsStatus = (state: RootState) =>
  state.dashboard.employeePartitions.status;

export const selectEmployeePartitionsRows = (state: RootState) =>
  state.dashboard.employeePartitions.rows;

export const selectEmployeePartitionsCols = (state: RootState) =>
  state.dashboard.employeePartitions.cols;

export const selectEmployeePartitions = createSelector(
  [
    (state: RootState) => state.dashboard.employeePartitions.items,
    (_: RootState, inject: Inject) => inject(PartitionMapper),
  ],
  (partitions, format) => format.processEmployeePartitions(partitions),
);

export const selectCompanyPartitionsStatus = (state: RootState) =>
  state.dashboard.companyPartitions.status;

export const selectCompanyPartitionsRows = (state: RootState) =>
  state.dashboard.companyPartitions.rows;

export const selectCompanyPartitionsCols = (state: RootState) =>
  state.dashboard.companyPartitions.cols;

export const selectCompanyPartitions = createSelector(
  [
    (state: RootState) => state.dashboard.companyPartitions.items,
    (state: RootState) => state.dashboard.companyPartitions.manageableCompanies,
    (_: RootState, inject: Inject) => inject(PartitionMapper),
  ],
  (partitions, companies, format) =>
    format.processCompanyPartitions(partitions, companies),
);

export const selectFullName = (state: RootState) => {
  const userData = state.user.data;

  if (userData === null) {
    return null;
  }

  return `${userData.first_name}${
    userData.use_full_greeting ? ` ${userData.middle_name}` : ''
  }`;
};

export const selectCalendarStatus = (state: RootState) =>
  state.dashboard.calendar.status;

export const selectCalendarVacationDays = createSelector(
  [
    (state: RootState) => state.dashboard.calendar.companies,
    (state: RootState) => state.dashboard.calendar.companyId,
    (state: RootState) => state.dashboard.calendar.includedEmployees,
    (_: RootState, inject: Inject) => inject(CalendarMapper),
  ],
  (companies, companyId, includedEmployees, mapper) =>
    mapper.processVacationDays(
      companies.find(({ id }) => id === companyId),
      includedEmployees,
    ),
);

export const selectCalendarCompanies = createSelector(
  [
    (state: RootState) => state.dashboard.calendar.companies,
    (_: RootState, inject: Inject) => inject(CalendarMapper),
  ],
  (companies, mapper) => mapper.processCompanies(companies),
);

export const selectCalendarCompanyId = (state: RootState) =>
  state.dashboard.calendar.companyId;

export const selectCalendarCompanyById = createSelector(
  [
    (state: RootState) => state.dashboard.calendar.companies,
    (state: RootState) => state.dashboard.calendar.companyId,
    (state: RootState) => state.dashboard.calendar.includedEmployees,
    (state: RootState) => state.dashboard.calendar.workSchedule,
    (_: RootState, inject: Inject) => inject(CalendarMapper),
  ],
  (companies, companyId, includedEmployees, workSchedule, mapper) =>
    mapper.processCompany(
      companies.find((company) => company.id === companyId),
      includedEmployees,
      workSchedule,
    ),
);

export const selectCalendarIncludedEmployees = (state: RootState) =>
  state.dashboard.calendar.includedEmployees;

export const selectCalendarWorkSchedule = createSelector(
  [
    (state: RootState) => state.dashboard.calendar.workSchedule,
    (_: RootState, inject: Inject) => inject(CalendarMapper),
  ],
  (workSchedule, mapper) =>
    workSchedule ? mapper.processWorkScheduleCalendar(workSchedule) : null,
);

export const selectAvailableQuickActionsWidgetId = createSelector(
  [(state: RootState) => state.dashboard.layout],
  (widgets) => {
    return widgets.find(
      (widget) => widget.type === 'quick_actions' && widget.is_active,
    )?.id;
  },
);

export const selectWidgetList = (state: RootState) => {
  return state.dashboard.widgetsSettings.widgets;
};

export const selectWidgetListStatus = (state: RootState) => {
  return state.dashboard.widgetsSettings.status;
};

export const selectDashboardWidgets = (state: RootState) => {
  const { layout } = state.dashboard;

  const quickActions = layout.find(({ type }) => type === 'quick_actions');
  let widgets = layout.filter(({ type }) => type !== 'quick_actions');

  if (quickActions) {
    widgets = [quickActions, ...widgets];
  }

  return widgets;
};

export const selectPersonalLinksSettingsStatus = (state: RootState) =>
  state.dashboard.personalLinksSettings.status;

export const selectPersonalLinksSettingsError = (state: RootState) =>
  state.dashboard.personalLinksSettings.error;

export const selectPersonalLinksSettingsInitialValues = createSelector(
  [
    (state: RootState) => state.dashboard.personalLinksSettings.name,
    (state: RootState) => state.dashboard.personalLinksSettings.links,
    (_: RootState, inject: Inject) => inject(PersonalLinksSettingsMapper),
  ],
  (name, links, mapper) => mapper.processSettingsInitialValues(name, links),
);

export const selectNewsWidgetSettingsStatus = (state: RootState) =>
  state.dashboard.newsSettings.status;

export const selectNewsWidgetSettingsError = (state: RootState) =>
  state.dashboard.newsSettings.error;

export const selectNewsWidgetSettingsFilterAvailable = (state: RootState) =>
  state.dashboard.newsSettings.isFilterAvailable;

export const selectNewsWidgetSettingsInitialValues = createSelector(
  [
    (state: RootState) => state.dashboard.newsSettings.name,
    (state: RootState) => state.dashboard.newsSettings.hasFilter,
  ],
  (name, hasFilter) => ({
    name: name || 'Новости',
    hasFilter: hasFilter.toString(),
  }),
);

export const selectExternalWidget = (widgetId: string) => (state: RootState) =>
  state.dashboard.external?.[widgetId];

export const selectExternalWidgetStatus =
  (widgetId: string) => (state: RootState) =>
    state.dashboard.external[widgetId]?.status;

export const selectWidgetBanner = (widgetId: string) =>
  createSelector(
    [
      (state: RootState) => state.dashboard.banner?.[widgetId],
      (_: RootState, inject: Inject) => inject(WidgetBannerMapper),
    ],
    (widget, mapper) => (widget ? mapper.processWidgetBanner(widget) : null),
  );

export const selectWidgetBannerStatus =
  (widgetId: string) => (state: RootState) =>
    state.dashboard.banner[widgetId]?.status;

export const selectBirthdaysStatus = (state: RootState) =>
  state.dashboard.birthdays.status;

export const selectBirthdaysEmployees = createSelector(
  [
    (state: RootState) => state.dashboard.birthdays.data,
    (_: RootState, inject: Inject) => inject(BirthdaysMapper),
  ],
  (data, mapper) => mapper.processBirthdaysList(data?.employees ?? []),
);

export const selectSurveysStatus = (state: RootState) =>
  state.dashboard.surveys.status;

export const selectSurveysItems = createSelector(
  [
    (state: RootState) => state.dashboard.surveys.items,
    (_: RootState, inject: Inject) => inject(SurveysWidgetListMapper),
  ],
  (surveysList, format) =>
    surveysList.map((surveysitem) => format.processSurveysList(surveysitem)) ||
    [],
);

export const selectSurveysSettingsStatus = (state: RootState) =>
  state.dashboard.surveysSettings.status;

export const selectSurveysSettingsError = (state: RootState) =>
  state.dashboard.surveysSettings.error;

export const selectSurveysSettingsInitialValues = createSelector(
  [(state: RootState) => state.dashboard.surveysSettings.name],
  (name) => {
    return { name };
  },
);

export const selectTopNewsStatus = (state: RootState) =>
  state.dashboard.topNews.status;

export const selectTopNews = createSelector(
  [
    (state: RootState) => state.dashboard.topNews.items,
    (_: RootState, inject: Inject) => inject(TopNewsMapper),
  ],
  (news, mapper) => mapper.processTopNewsItems(news),
);

export const selectQuickActionsSettingsStatus = (state: RootState) =>
  state.dashboard.quickActionsSettings.status;

export const selectQuickActionsSettingsError = (state: RootState) =>
  state.dashboard.quickActionsSettings.error;

export const selectQuickActionsSettingsInitialValues = createSelector(
  [
    (state: RootState) => state.dashboard.quickActionsSettings.userLinks,
    (_: RootState, inject: Inject) => inject(QuickActionsSettingsMapper),
  ],
  (links, mapper) => mapper.processSettingsInitialValues(links),
);

export const selectQuickActionsCompanyTitles = createSelector(
  [
    (state: RootState) => state.dashboard.quickActionsSettings.companyLinks,
    (_: RootState, inject: Inject) => inject(QuickActionsSettingsMapper),
  ],
  (actions, mapper) => mapper.processCompanyQuickActionsTitles(actions),
);

export const selectFeedPosts = createSelector(
  [
    (state: RootState) => state.dashboard.feedPostsList.posts,
    (_: RootState, inject: Inject) => inject(PostsFeedMapper),
  ],
  (posts, mapper) => mapper.proccessFeed(posts),
);

export const selectFeedPostStatus = (state: RootState) =>
  state.dashboard.feedPostsList.status;

export const selectFeedPostTotal = (state: RootState) =>
  state.dashboard.feedPostsList.total;

export const selectAvatars = (state: RootState) =>
  state.dashboard.feedPostsList.avatars.list;
